import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  GraduationCap,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  Eye,
  EyeOff,
  Mail,
  HelpCircle,
  Edit,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Degree type options
const DEGREE_TYPES = [
  { value: 'high_school', label: 'High School Diploma' },
  { value: 'associate', label: "Associate's Degree" },
  { value: 'bachelor', label: "Bachelor's Degree" },
  { value: 'master', label: "Master's Degree" },
  { value: 'doctorate', label: 'Doctorate' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'professional', label: 'Professional Degree (JD, MD, etc.)' },
  { value: 'other', label: 'Other' },
];

// Trust state badge styling (consistent with Certificates)
const getTrustStateBadge = (trustState) => {
  if (!trustState) return null;
  
  const { label, verified } = trustState;
  
  if (verified) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        <CheckCircle className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  }
  
  return (
    <Badge variant="secondary" className="bg-gray-100 text-gray-600">
      <Clock className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

export default function Education() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [verifying, setVerifying] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    institution: '',
    program: '',
    degree_type: 'bachelor',
    start_date: '',
    end_date: '',
    registrar_email: '',
    visibility_included: true,
  });
  
  // Verification email state
  const [verifyEmail, setVerifyEmail] = useState('');

  const fetchEducation = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/employee/education`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch education:', error);
      toast.error('Failed to load education history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEducation();
  }, [fetchEducation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/employee/education`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        toast.success('Education entry added');
        setShowAddDialog(false);
        setFormData({
          institution: '',
          program: '',
          degree_type: 'bachelor',
          start_date: '',
          end_date: '',
          registrar_email: '',
          visibility_included: true,
        });
        fetchEducation();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to add education entry');
      }
    } catch (error) {
      console.error('Failed to add education:', error);
      toast.error('Failed to add education entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this education entry?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/employee/education/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success('Education entry deleted');
        fetchEducation();
      } else {
        toast.error('Failed to delete education entry');
      }
    } catch (error) {
      console.error('Failed to delete education:', error);
      toast.error('Failed to delete education entry');
    }
  };

  const handleToggleVisibility = async (entry) => {
    try {
      const response = await fetch(`${API_URL}/api/employee/education/${entry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          visibility_included: !entry.visibility_included,
        }),
      });
      
      if (response.ok) {
        toast.success(entry.visibility_included ? 'Hidden from profile' : 'Visible on profile');
        fetchEducation();
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const openVerifyDialog = (entry) => {
    setSelectedEntry(entry);
    setVerifyEmail(entry.registrar_email || '');
    setShowVerifyDialog(true);
  };

  const handleRequestVerification = async () => {
    if (!selectedEntry || !verifyEmail) return;
    
    setVerifying(selectedEntry.id);
    
    try {
      const response = await fetch(
        `${API_URL}/api/employee/education/${selectedEntry.id}/request-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ registrar_email: verifyEmail }),
        }
      );
      
      if (response.ok) {
        toast.success('Verification request sent to registrar');
        setShowVerifyDialog(false);
        fetchEducation();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send verification request');
      }
    } catch (error) {
      console.error('Failed to request verification:', error);
      toast.error('Failed to send verification request');
    } finally {
      setVerifying(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Present';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
    });
  };

  const getDegreeLabel = (type) => {
    const degree = DEGREE_TYPES.find(d => d.value === type);
    return degree ? degree.label : type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="education-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Education History</h1>
          <p className="text-muted-foreground">
            Add and verify your educational background
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-education-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Education
        </Button>
      </div>

      {/* Info card about verification */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Education Verification</p>
              <p>
                Verified education entries carry more weight on your VETT score. 
                Request verification by sending an email to your institution&apos;s registrar. 
                They can confirm or dispute your claimed credentials.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Education entries list */}
      <div className="grid gap-4">
        {entries.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <GraduationCap className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No education history</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add your educational background to strengthen your profile
                </p>
                <Button variant="outline" onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Education
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          entries.map((entry) => (
            <Card key={entry.id} data-testid={`education-entry-${entry.id}`}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{entry.institution}</h3>
                        {getTrustStateBadge(entry.trust_state)}
                        {entry.verification_pending && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <Mail className="w-3 h-3 mr-1" />
                            Verification Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {getDegreeLabel(entry.degree_type)} in {entry.program}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(entry.start_date)} - {formatDate(entry.end_date)}
                      </p>
                      {entry.dispute_note && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          Dispute: {entry.dispute_note}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Visibility toggle */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleVisibility(entry)}
                      title={entry.visibility_included ? 'Hide from profile' : 'Show on profile'}
                    >
                      {entry.visibility_included ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                    
                    {/* Request verification (only for self-reported) */}
                    {entry.trust_state?.state === 'self_reported' && !entry.verification_pending && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openVerifyDialog(entry)}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Verify
                      </Button>
                    )}
                    
                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(entry.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Education Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Education</DialogTitle>
            <DialogDescription>
              Add your educational background. You can request verification from your institution later.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="institution">Institution *</Label>
                <Input
                  id="institution"
                  name="institution"
                  value={formData.institution}
                  onChange={handleInputChange}
                  placeholder="e.g., Stanford University"
                  required
                  data-testid="education-institution-input"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="program">Program / Major *</Label>
                <Input
                  id="program"
                  name="program"
                  value={formData.program}
                  onChange={handleInputChange}
                  placeholder="e.g., Computer Science"
                  required
                  data-testid="education-program-input"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="degree_type">Degree Type *</Label>
                <Select
                  value={formData.degree_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, degree_type: value }))}
                >
                  <SelectTrigger data-testid="education-degree-select">
                    <SelectValue placeholder="Select degree type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEGREE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    data-testid="education-start-date-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    name="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    placeholder="Leave blank if current"
                    data-testid="education-end-date-input"
                  />
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="registrar_email">Registrar Email (Optional)</Label>
                <Input
                  id="registrar_email"
                  name="registrar_email"
                  type="email"
                  value={formData.registrar_email}
                  onChange={handleInputChange}
                  placeholder="e.g., registrar@university.edu"
                  data-testid="education-registrar-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  You can add this later when requesting verification
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="visibility">Show on public profile</Label>
                  <p className="text-xs text-muted-foreground">
                    Control whether this appears on your public profile
                  </p>
                </div>
                <Switch
                  id="visibility"
                  checked={formData.visibility_included}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, visibility_included: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="education-submit-btn">
                Add Education
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Verification Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Request Verification</DialogTitle>
            <DialogDescription>
              Send a verification email to your institution&apos;s registrar. They will receive a link to confirm or dispute your education record.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {selectedEntry && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedEntry.institution}</p>
                <p className="text-sm text-muted-foreground">
                  {getDegreeLabel(selectedEntry.degree_type)} in {selectedEntry.program}
                </p>
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="verify_email">Registrar Email *</Label>
              <Input
                id="verify_email"
                type="email"
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                placeholder="registrar@university.edu"
                required
                data-testid="verify-registrar-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter the official email address of your institution&apos;s registrar office
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowVerifyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestVerification}
              disabled={!verifyEmail || verifying === selectedEntry?.id}
              data-testid="send-verification-btn"
            >
              {verifying === selectedEntry?.id ? (
                <>
                  <Mail className="w-4 h-4 mr-2 animate-pulse" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Verification
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
